'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SubmitBarProps {
  month: string;
  status: 'idle' | 'loading' | 'submitted';
  hasUnsaved: boolean;
  onSubmit: () => void;
  onSaveDraft: () => void;
}

export default function SubmitBar({ month, status, hasUnsaved, onSubmit, onSaveDraft }: SubmitBarProps) {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmitClick = () => {
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    onSubmit();
  };

  return (
    <>
      {/* Confirm modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-foreground/30 backdrop-blur-sm" onClick={() => setShowConfirm(false)} />
          <div className="relative bg-card rounded-2xl border border-border shadow-modal p-6 w-full max-w-sm animate-slide-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="PaperAirplaneIcon" size={20} variant="outline" className="text-primary" />
              </div>
              <div>
                <h3 className="text-base font-bold text-foreground">Submit Activity Log</h3>
                <p className="text-xs text-muted-foreground">{month} — Pune Shivajinagar BK Centre</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              Once submitted, your activity log for <strong className="text-foreground">{month}</strong> will be sent to your state coordinator for review. You can still edit it until it is verified.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="btn-secondary flex-1"
              >
                Go Back
              </button>
              <button
                onClick={handleConfirm}
                className="btn-primary flex-1"
              >
                <Icon name="PaperAirplaneIcon" size={16} variant="outline" />
                Confirm Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:left-64 transition-all duration-300">
        <div className="bg-card border-t border-border shadow-modal px-4 py-3 lg:px-8 xl:px-10 2xl:px-12">
          <div className="max-w-screen-2xl mx-auto flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex items-center gap-2">
                {status === 'submitted' ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--success-bg)] text-[var(--success)] text-xs font-semibold">
                    <Icon name="CheckCircleIcon" size={14} variant="outline" />
                    Submitted for {month}
                  </span>
                ) : hasUnsaved ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--warning-bg)] text-[var(--warning)] text-xs font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--warning)]" />
                    Unsaved changes
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-muted-foreground text-xs font-semibold">
                    <Icon name="ClockIcon" size={14} variant="outline" />
                    Draft — {month}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground hidden sm:block truncate">
                Pune Shivajinagar BK Centre · Maharashtra Zone
              </p>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {status !== 'submitted' && (
                <button
                  type="button"
                  onClick={onSaveDraft}
                  className="btn-secondary text-sm px-4 py-2"
                  disabled={status === 'loading'}
                >
                  <Icon name="BookmarkIcon" size={15} variant="outline" />
                  Save Draft
                </button>
              )}

              <button
                type="button"
                onClick={status === 'submitted' ? undefined : handleSubmitClick}
                disabled={status === 'loading' || status === 'submitted'}
                className="btn-primary text-sm px-5 py-2"
                style={{ minWidth: '140px' }}
              >
                {status === 'loading' ? (
                  <>
                    <Icon name="ArrowPathIcon" size={15} variant="outline" className="animate-spin" />
                    Submitting...
                  </>
                ) : status === 'submitted' ? (
                  <>
                    <Icon name="CheckCircleIcon" size={15} variant="outline" />
                    Submitted
                  </>
                ) : (
                  <>
                    <Icon name="PaperAirplaneIcon" size={15} variant="outline" />
                    Submit Log
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}